import { useCallback, useEffect, useState } from 'react'
import useGeoLocation from 'hooks/use-geo-location'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { processPredictions } from './utils'
import { getDetails, getGeocode, getLatLng } from 'use-places-autocomplete'
import { mapGoogleResultToAddressForm } from 'components/SearchPlace/helper'
import { CustomAddressConfiguration } from 'store/types/settings'
import { Prediction as CRMPredictions } from 'services/place-search'

export interface Location {
  lat: number
  lng: number
}

export interface LocationBias {
  radius: number
  center: google.maps.LatLng
}

export interface AutocompleteOptions {
  region?: string // e.g., 'us', 'gb'
  locationBias?: LocationBias
  locationRestriction?: LocationBias
  origin?: Location
  types?: string[] // e.g., ['geocode', 'establishment']
  componentRestrictions?: {
    country: string | string[]
  }
  strictBounds?: boolean
  sessionToken?: string
  language?: string // e.g., 'en', 'es'
  fields?: string[] // Fields to return
}

export interface Prediction {
  description: string
  place_id: string
  structured_formatting: {
    main_text: string
    secondary_text: string
    main_text_matched_substrings: Array<{
      offset: number
      length: number
    }>
  }
  terms: Array<{
    offset: number
    value: string
  }>
  types: string[]
  matched_substrings?: Array<{
    length: number
    offset: number
  }>
}

declare global {
  interface Window {
    google: typeof google
  }
}

export type GooglePlaceLibrary = 'drawing' | 'geometry' | 'localContext' | 'places' | 'visualization'

export const loadGoogleMaps = (apiKey: string, libraries: GooglePlaceLibrary[]): Promise<void> =>
  new Promise((resolve, reject) => {
    // If Google Maps is already loaded, resolve immediately
    if (window.google?.maps?.places) {
      resolve()
      return
    }

    const librariesParam = libraries.length > 0 ? `&libraries=${libraries.join(',')}` : 'places'

    // Check if script is already in the DOM
    if (document.querySelector("script[src*='maps.googleapis.com']")) {
      const checkLoaded = setInterval(() => {
        if (window.google?.maps?.places) {
          clearInterval(checkLoaded)
          resolve()
        }
      }, 100)
      return
    }

    // Create and append script
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=${librariesParam}&loading=async`
    script.async = true
    script.defer = true

    script.onload = () => {
      // Ensure google.maps is ready before resolving
      const checkMapsLoadedIntervalId = setInterval(() => {
        if (window.google?.maps?.places) {
          clearInterval(checkMapsLoadedIntervalId)
          resolve()
        }
      }, 100)

      // in case window.google?.maps?.places does not exists
      setTimeout(() => {
        if (checkMapsLoadedIntervalId) {
          clearInterval(checkMapsLoadedIntervalId)
          reject(new Error('Google Maps API did not load in time'))
        }
      }, 10000)
    }

    script.onerror = () => reject(new Error('Failed to load Google Maps script'))

    document.head.appendChild(script)
  })

export const useGooglePlacesAutocomplete = ({
  apiKey,
  libraries,
  countryFallback,
  addressConfigs,
}: {
  apiKey: string
  libraries: GooglePlaceLibrary[]
  countryFallback: string
  addressConfigs: CustomAddressConfiguration[]
}) => {
  const [service, setService] = useState<google.maps.places.AutocompleteService | null>(null)
  const queryClient = useQueryClient()
  const { currentLocation } = useGeoLocation()

  const handlePlaceDetailsWithGoogleAPI = async (rawPlace: CRMPredictions) => {
    try {
      const details = await getDetails({ placeId: rawPlace.place_id, fields: ['address_component'] })
      const geoCode = await getGeocode({ address: rawPlace.description })
      const coordinates = getLatLng(geoCode[0])
      const mapped = mapGoogleResultToAddressForm(details, addressConfigs)

      return {
        address: {
          ...mapped,
          ...coordinates,
          formatted: rawPlace.description,
        },
      }
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    if (!apiKey) {
      throw new Error('Google Maps API key is not configured')
    }

    let isMounted = true

    const initService = async () => {
      try {
        await loadGoogleMaps(apiKey, libraries)
        if (isMounted && window.google?.maps?.places) {
          setService(new google.maps.places.AutocompleteService())
        }
      } catch (error) {
        console.error('Failed to initialize Google Places:', error)
      }
    }

    initService()

    return () => {
      isMounted = false
    }
  }, [apiKey])

  const mutation = useMutation({
    mutationFn: async (params: { input: string; options?: AutocompleteOptions }) => {
      const { input, options } = params

      if (input == '') return []

      // Ensure service is initialized before making API call
      if (!service) throw new Error('Places service not initialized')

      // Make API request
      return new Promise<google.maps.places.AutocompletePrediction[]>((resolve) => {
        service.getPlacePredictions(
          {
            input,
            ...(options?.locationBias && {
              locationBias: options.locationBias,
            }),
            ...(options?.region && { region: options?.region }),
          },
          (predictions, status) => {
            if (status !== 'OK' || !predictions) {
              console.warn(`Places API returned no results: ${status}`)
              return resolve([])
            }
            queryClient.setQueryData(['placePredictions', input], predictions)

            resolve(predictions)
          },
        )
      })
    },
    onError: (error) => {
      console.error('Search failed:', error)
    },
    retry: (failureCount: number, error: Error) => {
      if (error.message === 'Places service not initialized' && failureCount < 2) {
        return true // Retry up to 2 times
      }
      return false
    },
    retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 5000),
    cacheTime: 60 * 60 * 1000,
  })

  const { mutateAsync, ...rest } = mutation

  const handleSearchWithGoogleAPI = useCallback(
    async (input: string) => {
      try {
        const isLocationAvailable = !!currentLocation.latitude && !!currentLocation.longitude

        let opt: AutocompleteOptions = {}

        if (
          isLocationAvailable &&
          typeof currentLocation.latitude == 'number' &&
          typeof currentLocation.longitude == 'number'
        ) {
          opt = {
            locationBias: {
              radius: 1000,
              center: new google.maps.LatLng(currentLocation.latitude, currentLocation.longitude),
            },
          }
        } else {
          opt = {
            region: countryFallback,
          }
        }
        // Check cache first
        const cachedData = queryClient.getQueryData<google.maps.places.AutocompletePrediction[]>([
          'placePredictions',
          input,
        ])

        if (cachedData) {
          return processPredictions(cachedData)
        } else {
          const res = await mutateAsync({
            input,
            options: opt,
          })
          return processPredictions(res)
        }
      } catch (error) {
        console.error('Search failed:', error)
        return []
      }
    },
    [currentLocation, countryFallback, queryClient, mutateAsync],
  )

  return {
    ...rest,
    isServiceReady: !!service,
    handleSearchWithGoogleAPI,
    handlePlaceDetailsWithGoogleAPI,
  }
}
